import type {Nodes as MarkdownNode} from 'mdast'
import {Node} from 'prosemirror-model'
import {Plugin} from 'prosemirror-state'
import {Decoration, DecorationSet} from 'prosemirror-view'
import {visit} from 'unist-util-visit'

import {getNodeSource, isPositionedNode, parseToMdast} from '../schema/utils'
import styles from './hybrid-block.module.css'

const inlineNodeElements = {
  strong: 'strong',
  emphasis: 'em',
  inlineCode: 'code',
  delete: 'del',
  link: 'a',
} as const satisfies Partial<Record<MarkdownNode['type'], keyof HTMLElementTagNameMap>>

type InlineNode = MarkdownNode & {type: keyof typeof inlineNodeElements}

function isInlineNode(node: MarkdownNode): node is InlineNode {
  return node.type in inlineNodeElements
}

function createInlineDecoration(parentText: string, parentPos: number, mdNode: InlineNode) {
  if (!isPositionedNode(mdNode)) return null

  const start = parentPos + mdNode.position.start.offset
  const end = parentPos + mdNode.position.end.offset
  const nodeSource = getNodeSource(mdNode, parentText)
  if (!nodeSource) return null

  const valueSource =
    'value' in mdNode ? mdNode.value : mdNode.children.map(child => getNodeSource(child, parentText)).join('')
  const valueIndex = nodeSource.indexOf(valueSource)

  const startDelimeter = nodeSource.slice(0, valueIndex)
  const endDelimeter = nodeSource.slice(valueIndex + valueSource.length)

  // FIXME: ProseMirror is rendering the format decoration as three separate elements, rather than containing the delimiters
  // inside the parent element.
  return [
    Decoration.inline(start, start + startDelimeter.length, {
      nodeName: 'span',
      class: styles.delimiter,
    }),
    Decoration.inline(start + startDelimeter.length, end - endDelimeter.length, {
      nodeName: inlineNodeElements[mdNode.type],
      href: mdNode.type === 'link' ? mdNode.url : undefined,
    }),
    Decoration.inline(end - endDelimeter.length, end, {
      nodeName: 'span',
      class: styles.delimiter,
    }),
  ]
}

const decorateInlineMarkdown = (doc: Node) => {
  const decorations: Decoration[] = []
  doc.descendants((node, pos) => {
    // this assumes inline content won't span across multiple text nodes, which might be reasonable as long as we
    // keep our parser working the way it currently does and also don't add any plugins that might split nodes
    if (node.isText && node.text) {
      const value = node.text
      const tree = parseToMdast(value)
      visit(tree, mdNode => {
        if (isInlineNode(mdNode)) decorations.push(...(createInlineDecoration(value, pos, mdNode) || []))
      })
    }
  })
  return DecorationSet.create(doc, decorations)
}

export const decorateInlineMarkdownPlugin = () =>
  new Plugin({
    state: {
      init(_, {doc}) {
        return decorateInlineMarkdown(doc)
      },
      apply(tr, oldDecorations) {
        if (tr.docChanged) {
          return decorateInlineMarkdown(tr.doc)
        }
        return oldDecorations
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)
      },
    },
  })
